import { useEffect, useRef, useState } from 'react';
import { Shield, ShieldCheck, Copy, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useMfaSetup, useMfaEnable, useMfaStatus } from '@/api/auth';
import { apiError } from '@/lib/api';
import { buildQRDataUrl } from '@/lib/qrcode';

interface Props {
  /** Called when MFA is successfully enabled or user chooses to skip. */
  onDone: () => void;
  /** Show a "Skip for now" button. Default true. */
  allowSkip?: boolean;
}

type Step = 'loading' | 'scan' | 'verify' | 'done';

export function MfaSetupModal({ onDone, allowSkip = true }: Props) {
  const mfaSetup = useMfaSetup();
  const mfaEnable = useMfaEnable();
  const mfaStatus = useMfaStatus();

  const [step, setStep] = useState<Step>('loading');
  const [secret, setSecret] = useState('');
  const [qrSrc, setQrSrc] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  // Auto-start setup when modal mounts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await mfaSetup.mutateAsync();
        if (cancelled) return;
        setSecret(data.secret);
        const src = await buildQRDataUrl(data.uri, 220);
        if (!cancelled) {
          setQrSrc(src);
          setStep('scan');
        }
      } catch (e) {
        if (!cancelled) setError(apiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Focus the code input when we reach the verify step
  useEffect(() => {
    if (step === 'verify') {
      setTimeout(() => codeRef.current?.focus(), 100);
    }
  }, [step]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mfaEnable.mutateAsync({ code });
      mfaStatus.refetch();
      setStep('done');
    } catch (e) {
      setError(apiError(e));
      setCode('');
      codeRef.current?.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
    >
      <div className="relative w-full max-w-md mx-4 bg-white shadow-2xl border border-slate-200">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-[#EFF0FF] flex items-center justify-center shrink-0">
            {step === 'done'
              ? <ShieldCheck size={20} className="text-green-600" />
              : <Shield size={20} className="text-[#0410BD]" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-[#12122C]">
              {step === 'done' ? 'You\'re protected!' : 'Set up two-factor authentication'}
            </h2>
            <p className="text-xs text-[#676687]">
              {step === 'done'
                ? 'Your account is now secured with an authenticator app.'
                : 'Use an authenticator app (Google Authenticator, Authy, Microsoft Authenticator) for extra security.'}
            </p>
          </div>
          {allowSkip && step !== 'done' && (
            <button
              type="button"
              onClick={onDone}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded transition-colors shrink-0"
              aria-label="Skip setup"
              title="Skip for now"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Step indicator */}
        {step !== 'done' && step !== 'loading' && (
          <div className="px-6 pt-5 pb-1">
            <div className="flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                step === 'verify' ? 'bg-green-500 text-white' : 'bg-[#0410BD] text-white'
              }`}>
                {step === 'verify' ? <Check size={13} /> : '1'}
              </div>
              <div className="flex-1 mx-2 h-0.5 bg-slate-100 relative overflow-hidden">
                <div className={`absolute inset-y-0 left-0 bg-green-500 transition-all duration-300 ${step === 'verify' ? 'right-0' : 'right-full'}`} />
              </div>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                step === 'verify' ? 'bg-[#0410BD] text-white' : 'bg-slate-100 text-slate-400'
              }`}>
                2
              </div>
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-[11px] text-slate-400">Scan QR code</span>
              <span className="text-[11px] text-slate-400">Verify code</span>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-5">

          {/* Loading */}
          {step === 'loading' && (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="w-8 h-8 border-2 border-[#0410BD] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-slate-500">Generating your setup code…</p>
            </div>
          )}

          {/* Scan step */}
          {step === 'scan' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">
                <strong>Step 1:</strong> Open your authenticator app and scan the QR code below, or enter the key manually.
              </p>

              <div className="flex justify-center">
                {qrSrc ? (
                  <img
                    src={qrSrc}
                    alt="QR code for authenticator setup"
                    className="w-[200px] h-[200px] border border-slate-200 p-1 rounded"
                  />
                ) : (
                  <div className="w-[200px] h-[200px] bg-slate-50 border border-slate-200 rounded flex items-center justify-center">
                    <p className="text-xs text-slate-400 text-center px-4">
                      QR unavailable — use the key below
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-1.5">Or enter this key manually:</p>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 overflow-hidden">
                  <code className="flex-1 text-sm font-mono tracking-wide text-[#12122C] whitespace-nowrap overflow-x-auto">
                    {secret.match(/.{1,4}/g)?.join(' ') ?? secret}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="shrink-0 text-slate-400 hover:text-[#0410BD] transition-colors"
                    title="Copy key"
                  >
                    {copied ? <Check size={15} className="text-green-500" /> : <Copy size={15} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Verify step */}
          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <p className="text-sm text-slate-700">
                <strong>Step 2:</strong> Enter the 6-digit code shown in your authenticator app to confirm it's set up correctly.
              </p>
              <div className="flex flex-col items-center gap-1">
                <Label htmlFor="mfa-modal-code">Verification code</Label>
                <Input
                  ref={codeRef}
                  id="mfa-modal-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000 000"
                  className="text-center font-mono tracking-[0.5em] text-lg max-w-[180px]"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded">
                  {error}
                </div>
              )}
            </form>
          )}

          {/* Done */}
          {step === 'done' && (
            <div className="text-center py-4 space-y-3">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <ShieldCheck size={32} className="text-green-600" />
              </div>
              <p className="text-sm text-slate-600">
                Two-factor authentication is enabled. You'll be asked for a code from your authenticator app each time you sign in.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50">
          {step === 'scan' && (
            <>
              {allowSkip ? (
                <button
                  type="button"
                  onClick={onDone}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Skip for now
                </button>
              ) : <span />}
              <Button
                variant="primary"
                onClick={() => { setError(null); setCode(''); setStep('verify'); }}
                className="w-auto"
              >
                I've scanned it — Next
              </Button>
            </>
          )}

          {step === 'verify' && (
            <>
              <button
                type="button"
                onClick={() => { setError(null); setStep('scan'); }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                ← Back
              </button>
              <Button
                variant="primary"
                onClick={handleVerify as unknown as React.MouseEventHandler}
                loading={mfaEnable.isPending}
                disabled={code.length !== 6}
                className="w-auto"
              >
                Enable 2FA
              </Button>
            </>
          )}

          {step === 'done' && (
            <div className="flex justify-end w-full">
              <Button variant="primary" onClick={onDone} className="w-auto">
                Continue to app
              </Button>
            </div>
          )}

          {step === 'loading' && (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}
